/**
 * Поповер — базовый примитив для <Select>, <Menu> и <Tooltip> (§2.3).
 *
 * Что он делает и почему именно так:
 *
 * • Позиционирование считается руками через `getBoundingClientRect` — внешних
 *   зависимостей в проекте нет. Слой выводится в портал на `document.body`,
 *   иначе `overflow` таблицы обрежет список значений.
 * • Переворот: не хватает места снизу и хватает сверху — открывается над
 *   триггером (§2.3). По горизонтали слой вжимается в экран с полем 8 px.
 * • Esc и клик вне закрывают **верхний** слой (см. overlay-stack): поповер
 *   внутри модалки не должен закрывать модалку вместе с собой.
 * • Фокус возвращается на триггер при закрытии (§13, «поповеры и меню»).
 * • Пересчёт — на скролле (capture: таблица скроллится внутри себя), на
 *   ресайзе и на изменении размера содержимого.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { isTopLayer, popLayer, pushLayer } from './overlay-stack'
import './Popover.css'

export type PopoverPlacement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
export type PopoverRole = 'listbox' | 'menu' | 'dialog' | 'tooltip' | 'none'

export interface PopoverProps {
  open: boolean
  /** Элемент-триггер: относительно него считается позиция. */
  anchor: HTMLElement | null
  onClose: () => void
  children: ReactNode
  /** Роль слоя для скринридера (§13). `none` — слой без роли, роль на содержимом. */
  role?: PopoverRole
  placement?: PopoverPlacement
  /** Ширина не меньше ширины триггера (§2.3: `max(ширина ячейки, 200px)`). */
  matchAnchorWidth?: boolean
  minWidth?: number
  maxWidth?: number
  /** Зазор между триггером и слоем. */
  offset?: number
  /** Перевести фокус внутрь слоя при открытии (меню — да, селект — нет). */
  focusOnOpen?: boolean
  /** Вернуть фокус на триггер при закрытии. */
  returnFocus?: boolean
  closeOnOutsideClick?: boolean
  closeOnEsc?: boolean
  /** Поповер без своей поверхности: фон и рамку рисует содержимое (тултип). */
  bare?: boolean
  labelledBy?: string
  describedBy?: string
  activeDescendant?: string
  className?: string
}

/** Поле от края окна: слой никогда не прилипает к границе вьюпорта. */
const VIEWPORT_MARGIN = 8

interface Position {
  top: number
  left: number
  width: number | null
  maxHeight: number
  placement: PopoverPlacement
}

export function Popover({
  open,
  anchor,
  onClose,
  children,
  role = 'none',
  placement = 'bottom-start',
  matchAnchorWidth = false,
  minWidth,
  maxWidth,
  offset = 4,
  focusOnOpen = false,
  returnFocus = true,
  closeOnOutsideClick = true,
  closeOnEsc = true,
  bare = false,
  labelledBy,
  describedBy,
  activeDescendant,
  className,
}: PopoverProps) {
  const layerRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState<Position | null>(null)

  const measure = useCallback(() => {
    const layer = layerRef.current
    if (!anchor || !layer) return

    const a = anchor.getBoundingClientRect()
    const viewportW = window.innerWidth
    const viewportH = window.innerHeight

    const width = matchAnchorWidth
      ? Math.min(Math.max(a.width, minWidth ?? 0), maxWidth ?? Number.POSITIVE_INFINITY)
      : null

    /* Мерим ПОЛНУЮ высоту содержимого (scrollHeight + рамки), а не текущую:
       после первого прохода слой уже ограничен max-height, и offsetHeight
       навсегда зафиксировал бы обрезанный размер — при увеличении окна поповер
       больше не расправился бы. */
    const chrome = layer.offsetHeight - layer.clientHeight
    const h = layer.scrollHeight + chrome
    const w = width ?? layer.offsetWidth

    const wantsTop = placement.startsWith('top')
    const spaceBelow = viewportH - a.bottom - offset - VIEWPORT_MARGIN
    const spaceAbove = a.top - offset - VIEWPORT_MARGIN

    /* Переворот: только если на другой стороне места действительно больше. */
    let onTop = wantsTop
    if (!wantsTop && h > spaceBelow && spaceAbove > spaceBelow) onTop = true
    if (wantsTop && h > spaceAbove && spaceBelow > spaceAbove) onTop = false

    const available = onTop ? spaceAbove : spaceBelow
    const maxHeight = Math.max(available, 0)
    const height = Math.min(h, maxHeight)

    const top = onTop ? a.top - offset - height : a.bottom + offset

    const wantsEnd = placement.endsWith('end')
    let left = wantsEnd ? a.right - w : a.left
    left = Math.min(left, viewportW - VIEWPORT_MARGIN - w)
    left = Math.max(left, VIEWPORT_MARGIN)

    const side: PopoverPlacement = onTop
      ? wantsEnd ? 'top-end' : 'top-start'
      : wantsEnd ? 'bottom-end' : 'bottom-start'

    setPosition({ top: Math.round(top), left: Math.round(left), width, maxHeight, placement: side })
  }, [anchor, matchAnchorWidth, maxWidth, minWidth, offset, placement])

  /* Первый проход: слой отрисован скрытым, размеры сняты, позиция поставлена. */
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }
    measure()
  }, [open, measure, children])

  /* Пересчёт на скролле и ресайзе. Скролл слушаем в capture: таблица и панель
     скроллятся внутри себя, и такие события до window без capture не доходят. */
  useEffect(() => {
    if (!open) return
    const onScrollOrResize = () => measure()
    window.addEventListener('scroll', onScrollOrResize, { capture: true, passive: true })
    window.addEventListener('resize', onScrollOrResize)

    let observer: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined' && layerRef.current) {
      observer = new ResizeObserver(onScrollOrResize)
      observer.observe(layerRef.current)
    }

    return () => {
      window.removeEventListener('scroll', onScrollOrResize, { capture: true })
      window.removeEventListener('resize', onScrollOrResize)
      observer?.disconnect()
    }
  }, [open, measure])

  /* Слой в стеке: Esc и клик вне адресованы только верхнему слою.
     Идентификатор держим в ref — обработчики читают актуальный, не замкнутый. */
  const layerIdRef = useRef<symbol | null>(null)
  useEffect(() => {
    if (!open) {
      layerIdRef.current = null
      return
    }
    const id = pushLayer('popover')
    layerIdRef.current = id
    return () => {
      popLayer(id)
      layerIdRef.current = null
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    const isTop = () => {
      const id = layerIdRef.current
      return id != null && isTopLayer(id)
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (!closeOnEsc || event.key !== 'Escape' || !isTop()) return
      event.preventDefault()
      onClose()
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!closeOnOutsideClick || !isTop()) return
      const target = event.target
      if (!(target instanceof Node)) return
      if (layerRef.current?.contains(target)) return
      if (anchor?.contains(target)) return
      onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open, anchor, closeOnEsc, closeOnOutsideClick, onClose])

  /* Фокус: внутрь слоя при открытии (меню), обратно на триггер при закрытии. */
  useEffect(() => {
    if (!open) return
    const trigger = anchor
    if (focusOnOpen) layerRef.current?.focus()
    return () => {
      if (!returnFocus || !trigger) return
      const active = document.activeElement
      /* Возврат только если фокус остался внутри слоя или улетел в body:
         клик по другому контролу не должен отбирать у него фокус. */
      if (active === document.body || (active instanceof Node && layerRef.current?.contains(active))) {
        trigger.focus()
      }
    }
  }, [open, anchor, focusOnOpen, returnFocus])

  if (!open) return null

  const classes = ['popover']
  if (bare) classes.push('popover--bare')
  if (!position) classes.push('popover--measuring')
  if (className) classes.push(className)

  return createPortal(
    <div
      ref={layerRef}
      className={classes.join(' ')}
      data-placement={position?.placement ?? placement}
      role={role === 'none' ? undefined : role}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-activedescendant={activeDescendant}
      tabIndex={focusOnOpen ? -1 : undefined}
      style={{
        top: position ? `${position.top}px` : 0,
        left: position ? `${position.left}px` : 0,
        width: position?.width != null ? `${position.width}px` : undefined,
        minWidth: minWidth != null ? `${minWidth}px` : undefined,
        maxWidth: maxWidth != null ? `${maxWidth}px` : undefined,
        maxHeight: position ? `${position.maxHeight}px` : undefined,
      }}
    >
      {children}
    </div>,
    document.body,
  )
}
